/** Pure rules behind the Insurance cover section of the subcontractor sheet:
 * how a policy reads, the order it is listed in, what a renewal starts from and
 * how a typed-in policy becomes a request body. The server re-checks every
 * field; these checks exist so a typo is caught before the round trip.
 */

import { describeDaysLeft } from '../../utils/describeDaysLeft';

export type CoverType =
  | 'public_liability'
  | 'employers_liability'
  | 'professional_indemnity'
  | 'contract_works'
  | 'other';

export type CoverStatus = 'expired' | 'expiring' | 'current' | 'superseded';

export interface SubcontractorCover {
  id: string;
  subcontractorId: string;
  coverType: CoverType;
  insurer: string;
  policyNumber: string;
  indemnityLimit: number | null;
  startDate: string | null;
  endDate: string;
  lifecycleStatus: 'active' | 'superseded';
  notes: string;
  status: CoverStatus;
  daysLeft: number;
}

/** A row of the workspace-wide expiring list: the policy plus whose it is. */
export interface ExpiringSubcontractorCover extends SubcontractorCover {
  subcontractorName: string;
}

export const COVER_TYPE_OPTIONS: ReadonlyArray<{ value: CoverType; label: string }> = [
  { value: 'public_liability', label: 'Public liability' },
  { value: 'employers_liability', label: 'Employers liability' },
  { value: 'professional_indemnity', label: 'Professional indemnity' },
  { value: 'contract_works', label: 'Contract works' },
  { value: 'other', label: 'Other' },
];

export function coverTypeLabel(type: CoverType): string {
  return COVER_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? 'Other';
}

/** What the status badge says. A superseded policy is history, so it gives no countdown. */
export function describeCover(cover: Pick<SubcontractorCover, 'status' | 'daysLeft'>): string {
  if (cover.status === 'superseded') return 'Superseded';
  const text = describeDaysLeft(cover.daysLeft);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Active policies first, soonest end first (the one to chase), then superseded
 * ones, most recent first. */
export function orderCover(list: readonly SubcontractorCover[]): SubcontractorCover[] {
  return [...list].sort((a, b) => {
    const aOld = a.lifecycleStatus === 'superseded';
    const bOld = b.lifecycleStatus === 'superseded';
    if (aOld !== bOld) return aOld ? 1 : -1;
    return aOld ? b.endDate.localeCompare(a.endDate) : a.endDate.localeCompare(b.endDate);
  });
}

/** What the form edits. Money is text so a half-typed number is not rewritten under the cursor. */
export interface CoverDraft {
  coverType: CoverType;
  insurer: string;
  policyNumber: string;
  indemnityLimit: string;
  startDate: string;
  endDate: string;
  notes: string;
}

export const EMPTY_COVER_DRAFT: CoverDraft = {
  coverType: 'public_liability',
  insurer: '',
  policyNumber: '',
  indemnityLimit: '',
  startDate: '',
  endDate: '',
  notes: '',
};

/** A new policy usually comes from the insurer already on file, so the form starts
 * with the insurer of whichever policy ended last. The policy number is left blank:
 * it differs for every policy, even a combined one. */
export function addCoverDraft(existing: readonly SubcontractorCover[]): CoverDraft {
  const latest = existing.reduce<SubcontractorCover | null>(
    (best, cover) => (best === null || cover.endDate > best.endDate ? cover : best),
    null,
  );
  return { ...EMPTY_COVER_DRAFT, insurer: latest?.insurer ?? '' };
}

export function draftFromCover(cover: SubcontractorCover): CoverDraft {
  return {
    coverType: cover.coverType,
    insurer: cover.insurer,
    policyNumber: cover.policyNumber,
    indemnityLimit: cover.indemnityLimit === null ? '' : String(cover.indemnityLimit),
    startDate: cover.startDate ?? '',
    endDate: cover.endDate,
    notes: cover.notes,
  };
}

function shiftDay(day: string, years: number, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  const month = d.getUTCMonth();
  d.setUTCFullYear(d.getUTCFullYear() + years);
  // 29 February plus a year rolls into March; a policy renewing "on the last day
  // of February" should stay in February.
  if (years !== 0 && d.getUTCMonth() !== month) d.setUTCDate(0);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A renewal starts the day after the old policy ends and runs a year, the usual
 * UK term. Insurer, policy number and limit carry over to be overtyped. */
export function renewalDraft(cover: SubcontractorCover): CoverDraft {
  return {
    ...draftFromCover(cover),
    startDate: shiftDay(cover.endDate, 0, 1),
    endDate: shiftDay(cover.endDate, 1, 0),
    notes: '',
  };
}

export interface CoverPayload {
  coverType: CoverType;
  insurer: string;
  policyNumber: string;
  indemnityLimit: number | null;
  startDate: string | null;
  endDate: string;
  notes: string;
}

export function draftToPayload(
  draft: CoverDraft,
): { ok: true; value: CoverPayload } | { ok: false; error: string } {
  if (!draft.endDate) return { ok: false, error: 'Enter the date the cover ends' };
  if (draft.startDate && draft.startDate > draft.endDate) {
    return { ok: false, error: 'The start date must not be after the end date' };
  }
  const limitText = draft.indemnityLimit.replace(/[£,\s]/g, '');
  let indemnityLimit: number | null = null;
  if (limitText !== '') {
    indemnityLimit = Number(limitText);
    if (!Number.isFinite(indemnityLimit) || indemnityLimit < 0) {
      return { ok: false, error: 'Enter the indemnity limit as an amount of zero or more' };
    }
  }
  return {
    ok: true,
    value: {
      coverType: draft.coverType,
      insurer: draft.insurer.trim(),
      policyNumber: draft.policyNumber.trim(),
      indemnityLimit,
      startDate: draft.startDate || null,
      endDate: draft.endDate,
      notes: draft.notes.trim(),
    },
  };
}
